import React from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Text,
  VStack,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react';
import { FiRefreshCw, FiMenu } from 'react-icons/fi';
import SaktiSidebar from '../components/SaktiSidebar';
import SaktiRkhFilterBar from '../components/SaktiRkhFilterBar';
import SaktiRkhKpiCards from '../components/SaktiRkhKpiCards';
import SaktiRkhTabsTable from '../components/SaktiRkhTabsTable';
import SaktiRkhDetailModal from '../components/SaktiRkhDetailModal';
import { useSaktiRkhDashboard } from '../hooks/useSaktiRkhDashboard';
import { SAKTI_UI_SIZE } from '../uiSizes';

const SaktiRkhPage: React.FC = () => {
  const {
    options,
    loadingOptions,
    loadingKpi,
    loadingTable,
    activeTab,
    draftFilters,
    kpi,
    rkhRows,
    harvesterRows,
    rkhPagination,
    harvesterPagination,
    tableError,
    detailLoading,
    detailData,
    isDetailOpen,
    isEditMode,
    isSavingDetail,
    editDraft,
    isDetailDirty,
    isDetailValid,
    onDraftFilterChange,
    applyFilters,
    setActiveTab,
    goToPage,
    openDetail,
    closeDetail,
    startDetailEdit,
    cancelDetailEdit,
    updateDetailDraft,
    saveDetailEdit,
    reloadAll,
  } = useSaktiRkhDashboard();

  const { 
    isOpen: isMobileSidebarOpen, 
    onOpen: onMobileSidebarOpen, 
    onClose: onMobileSidebarClose 
  } = useDisclosure();

  return (
    <Box minH="100vh" bg="#ECEEF1">
      <Flex align="stretch">
        {/* Desktop Sidebar */}
        <Box display={{ base: 'none', xl: 'block' }}>
          <SaktiSidebar activeMenu="rkh" />
        </Box>

        {/* Mobile Sidebar Drawer */}
        <Drawer
          isOpen={isMobileSidebarOpen}
          placement="left"
          onClose={onMobileSidebarClose}
          size="xs"
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton zIndex={10} color="gray.500" />
            <DrawerBody p={0}>
              <SaktiSidebar activeMenu="rkh" />
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        <Box flex={1} p={SAKTI_UI_SIZE.pagePadding}>
          <VStack align="stretch" spacing={SAKTI_UI_SIZE.sectionGap}>
            <HStack justify="space-between" align={{ base: 'start', md: 'center' }} flexWrap="wrap">
              <HStack spacing={3} align="center">
                <IconButton
                  aria-label="Open menu"
                  icon={<FiMenu />}
                  variant="outline"
                  bg="white"
                  display={{ base: 'flex', xl: 'none' }}
                  onClick={onMobileSidebarOpen}
                />
                <Text fontSize={SAKTI_UI_SIZE.pageTitle} fontWeight="bold" color="gray.900" lineHeight="shorter">
                  Rencana Kerja Harian (RKH)
                </Text>
              </HStack>
              <Button leftIcon={<FiRefreshCw />} onClick={reloadAll} variant="outline" size="sm" bg="white" mt={{ base: 2, md: 0 }}>
                Refresh
              </Button>
            </HStack>

            <SaktiRkhFilterBar
              filters={draftFilters}
              options={options}
              onFiltersChange={onDraftFilterChange}
              onApply={applyFilters}
              isApplying={loadingOptions || loadingTable}
            />

            <SaktiRkhKpiCards loading={loadingKpi || loadingOptions} data={kpi} />

            {tableError && (
              <Alert status="warning" borderRadius="lg">
                <AlertIcon />
                {tableError}
              </Alert>
            )}

            <SaktiRkhTabsTable
              loading={loadingTable || loadingOptions}
              activeTab={activeTab}
              rkhRows={rkhRows}
              harvesterRows={harvesterRows}
              rkhPagination={rkhPagination}
              harvesterPagination={harvesterPagination}
              onTabChange={setActiveTab}
              onPageChange={goToPage}
              onViewDetail={openDetail}
            />
          </VStack>
        </Box>
      </Flex>

      <SaktiRkhDetailModal
        isOpen={isDetailOpen}
        loading={detailLoading}
        detailData={editDraft ?? detailData}
        onClose={closeDetail}
        isEditMode={isEditMode}
        isSaving={isSavingDetail}
        isDirty={isDetailDirty}
        isValid={isDetailValid}
        onStartEdit={startDetailEdit}
        onCancelEdit={cancelDetailEdit}
        onSaveEdit={saveDetailEdit}
        onDraftChange={updateDetailDraft}
      />
    </Box>
  );
};

export default SaktiRkhPage;
